// The photo editors' edit drawer (Studio Editor, AI photo editor) — docked to the bottom edge of the photo, laid out like a phone photo
// app: the chosen tool's name and value, a ruler slider, a row of round tool buttons, and the tabs (Adjust / Filters / ঘোরান). Closed
// by default; the page's "ছবি এডিটিং" button (EditToggle) opens it. The edits are never saved here — the page bakes them into the
// instant download, print and copy (utils/photoEdits.js) and shows them live through a re-rendered preview of the result.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    IconAdjustments, IconAperture, IconChevronDown, IconCircleDashed, IconColorFilter, IconContrast, IconCrop, IconDroplet, IconFlipVertical,
    IconFocus2, IconRotate, IconRotate2, IconRotateClockwise2, IconSun, IconSunHigh, IconSunLow, IconTemperature, IconX,
} from '@tabler/icons-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import RulerSlider from '@/components/common/RulerSlider';
import { useDragWheelScroll } from '../../lib/hooks';
import { toBnDigits } from '../../utils/bnFormat';
import { EDIT_TOOLS, FILTER_PRESETS, NEUTRAL_EDITS, isEdited } from '../../utils/photoEdits';

const TOOL_ICONS = {
    warmth: IconTemperature, brightness: IconSun, exposure: IconAperture, contrast: IconContrast, highlights: IconSunHigh,
    shadows: IconSunLow, saturation: IconDroplet, sharpness: IconFocus2, vignette: IconCircleDashed,
};
const EDIT_TABS = [
    { key: 'adjust', Icon: IconAdjustments },
    { key: 'filters', Icon: IconColorFilter },
    { key: 'crop', Icon: IconRotateClockwise2 }, // crop · rotate · flip, labelled "ঘোরান"
];

// A round tool button with its label under it (the phone-app look); `on` = selected, `dot` = this tool has a value.
export function RoundTool({ Icon, label, on = false, dot = false, onClick, disabled = false }) {
    return (
        <button type="button" onClick={onClick} disabled={disabled} aria-pressed={on} className="group flex w-[52px] shrink-0 flex-col items-center gap-0.5 disabled:opacity-40">
            <span
                className={cn(
                    'relative flex h-9 w-9 items-center justify-center rounded-full border transition-colors',
                    on
                        ? 'border-primary bg-primary text-white shadow-md shadow-black/30'
                        : 'border-white/15 bg-white/10 text-white group-hover:border-white/30 group-hover:bg-white/20',
                )}
            >
                <Icon size={17} strokeWidth={1.8} />
                {dot && !on && <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-primary" />}
            </span>
            <span className={cn('w-full truncate text-center text-[10px] font-medium leading-tight', on ? 'text-orange-300' : 'text-white/80')}>{label}</span>
        </button>
    );
}

/**
 * @param value / onChange   the edits (utils/photoEdits.js)
 * @param open / onClose     the drawer's state (the page owns it)
 * @param thumbs             filter thumbnails by preset id (useFilterThumbs)
 * @param onCrop             the ঘোরান tab's Crop button — the page decides what a crop is (Studio: a crop of the download only; AI
 *                           editor: the saved result crop); omitted = no Crop button
 * @param cropped / onClearCrop   a page-held crop is applied → its "remove crop" button (omit when the crop is saved instead)
 */
export default function PhotoEditDrawer({ value, onChange, open, onClose, thumbs = {}, cropped = false, onCrop, onClearCrop }) {
    const { t, i18n } = useTranslation();
    const num = (n) => (i18n.language === 'bn' ? toBnDigits(n) : String(n));
    const [tab, setTab] = useState('adjust');
    const [tool, setTool] = useState(EDIT_TOOLS[0].key);
    const toolDef = EDIT_TOOLS.find((x) => x.key === tool) || EDIT_TOOLS[0];
    const signed = (v) => (v > 0 ? `+${num(v)}` : v < 0 ? `−${num(-v)}` : num(0));
    const set = (patch) => onChange({ ...value, ...patch });
    const edited = isEdited(value) || cropped;
    const hasFilter = value.filter !== 'none';
    // The header's middle: what the ruler below is changing, and its value
    const caption = tab === 'adjust'
        ? [t(`studioEditor.edit.tool_${tool}`), signed(value[tool] || 0)]
        : tab === 'filters' && hasFilter ? [t(`studioEditor.edit.filter_${value.filter}`), `${num(value.filterStrength)}%`] : null;
    const iconBtn = 'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-white/80 transition-colors hover:bg-white/15 hover:text-white';
    // The tool / filter / crop rows overflow sideways: mouse wheel + drag slide them smoothly.
    const adjustStripRef = useDragWheelScroll();
    const filterStripRef = useDragWheelScroll();
    const cropStripRef = useDragWheelScroll();

    return (
        <div
            className={cn(
                // see-through glass, so the photo shows under it; one compact header row (tabs · caption · reset / close), the ruler, a tool row
                // dark see-through glass (like a phone photo editor): white controls stay readable on any photo, and the photo shows through
                'absolute inset-x-0 bottom-0 z-20 bg-gradient-to-t from-black/60 via-black/40 to-black/15 px-2 pb-1.5 pt-1.5 text-white backdrop-blur-[2px] transition-transform duration-300 ease-out',
                !open && 'translate-y-full',
            )}
            inert={open ? undefined : ''}
            aria-hidden={!open || undefined}
        >
            {/* Header: the caption (what the ruler changes + its value) · the tabs, centred · reset / close */}
            <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-1">
                <p className="min-w-0 truncate pl-1 text-xs !font-semibold !text-white">
                    {caption && <>{caption[0]} <span className="tabular-nums text-orange-300">{caption[1]}</span></>}
                </p>
                <div className="flex items-center gap-0.5">
                    {EDIT_TABS.map((x) => (
                        <button
                            key={x.key}
                            type="button"
                            onClick={() => setTab(x.key)}
                            aria-pressed={tab === x.key}
                            className={cn(
                                'inline-flex h-7 shrink-0 items-center gap-1 rounded-md px-2 text-[11px] font-semibold transition-colors',
                                tab === x.key ? 'bg-primary text-white shadow-md shadow-black/30' : 'text-white/75 hover:bg-white/15 hover:text-white',
                            )}
                        >
                            <x.Icon size={14} />
                            {t(`studioEditor.edit.tab_${x.key}`)}
                        </button>
                    ))}
                </div>
                <div className="flex items-center justify-end">
                    {edited && (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button type="button" onClick={() => { onChange(NEUTRAL_EDITS); if (cropped) onClearCrop?.(); }} aria-label={t('studioEditor.adjustReset')} className={iconBtn}>
                                    <IconRotate size={15} />
                                </button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="z-[2000]">{t('studioEditor.adjustReset')}</TooltipContent>
                        </Tooltip>
                    )}
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button type="button" onClick={onClose} aria-label={t('common.close')} className={iconBtn}>
                                <IconChevronDown size={16} />
                            </button>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="z-[2000]">{t('common.close')}</TooltipContent>
                    </Tooltip>
                </div>
            </div>

            {tab === 'adjust' && (
                <>
                    <RulerSlider className="h-7" onDark value={value[tool] || 0} min={toolDef.min} max={toolDef.max} neutral={0} onChange={(v) => set({ [tool]: v })} ariaLabel={t(`studioEditor.edit.tool_${tool}`)} />
                    <div ref={adjustStripRef} className="-mx-2 overflow-x-auto px-2 [scrollbar-width:none]">
                        <div className="mx-auto flex w-max gap-0.5">
                            {EDIT_TOOLS.map((x) => (
                                <RoundTool key={x.key} Icon={TOOL_ICONS[x.key]} label={t(`studioEditor.edit.tool_${x.key}`)} on={x.key === tool} dot={(value[x.key] || 0) !== 0} onClick={() => setTool(x.key)} />
                            ))}
                        </div>
                    </div>
                </>
            )}

            {tab === 'filters' && (
                <>
                    {hasFilter && (
                        <RulerSlider className="h-7" onDark value={value.filterStrength} min={0} max={100} neutral={100} onChange={(v) => set({ filterStrength: v })} ariaLabel={t('studioEditor.edit.strength')} />
                    )}
                    <div ref={filterStripRef} className={cn('-mx-2 overflow-x-auto px-2 [scrollbar-width:none]', !hasFilter && 'pt-1')}>
                        <div className="mx-auto flex w-max gap-1.5">
                            {FILTER_PRESETS.map((f) => {
                                const on = value.filter === f.id;
                                return (
                                    <button key={f.id} type="button" onClick={() => set({ filter: f.id, filterStrength: on ? value.filterStrength : 100 })} aria-pressed={on} className="flex w-[46px] shrink-0 flex-col items-center gap-0.5">
                                        <span className={cn('block h-[46px] w-[46px] overflow-hidden rounded-lg border-2 bg-white/10', on ? 'border-primary shadow-md shadow-black/30' : 'border-transparent')}>
                                            {thumbs[f.id] && <img src={thumbs[f.id]} alt="" draggable="false" className="h-full w-full object-cover" />}
                                        </span>
                                        <span className={cn('w-full truncate text-center text-[10px] font-medium leading-tight', on ? 'text-orange-300' : 'text-white/80')}>{t(`studioEditor.edit.filter_${f.id}`)}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </>
            )}

            {tab === 'crop' && (
                <div ref={cropStripRef} className="-mx-2 overflow-x-auto px-2 pt-1 [scrollbar-width:none]">
                    <div className="mx-auto flex w-max gap-0.5">
                        {onCrop && <RoundTool Icon={IconCrop} label={t('studioEditor.edit.crop')} dot={cropped} onClick={onCrop} />}
                        <RoundTool Icon={IconRotate2} label={t('studioEditor.edit.rotateLeft')} onClick={() => set({ rotate: ((value.rotate || 0) + 270) % 360 })} />
                        <RoundTool Icon={IconRotateClockwise2} label={t('studioEditor.edit.rotateRight')} onClick={() => set({ rotate: ((value.rotate || 0) + 90) % 360 })} />
                        <RoundTool Icon={IconFlipVertical} label={t('studioEditor.edit.flip')} on={!!value.flipH} onClick={() => set({ flipH: !value.flipH })} />
                        {cropped && <RoundTool Icon={IconX} label={t('studioEditor.edit.clearCrop')} onClick={onClearCrop} />}
                    </div>
                </div>
            )}
        </div>
    );
}
